import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { LoadingIndicator } from '@components/LoadingIndicatorMaterial';
import IngestionOverrideForm, {
  IngestionScheduleFormData,
} from 'Modules/Studio/components/IngestionOverrideForm/IngestionOverrideForm';
import { useGlobalIngestionSchedule } from '../hooks/useGlobalIngestionSchedule';
import { CancelButton, SubmitButton } from '@3.0/Buttons';
import { ActionButtonsContainer } from 'Modules/Studio/components/StudioModal';
import { Animated } from '@components/Animated';
import FormDivider from '@components/Blackbird/Forms/FormDivider';
import InfoBanner from '@3.0/InfoBanner';
import { TInfoBannerVariant } from '@3.0/InfoBanner/InfoBanner';
import { EmptyListSection } from '@3.0/index';
import { useIngestionSchedule } from './context/IngestionSchedule.context';
import { selectHasPermissions } from '@redux/Roles/roles.selectors';
import { StudioRoutes } from 'Routing/RouteConstants';
import { IngestionScheduleSummary } from './components/IngestionScheduleSummary';
import { useAppSelector } from '@redux/store';

export default function IngestionSchedule() {
  const canEdit = useAppSelector((state) =>
    selectHasPermissions(state, StudioRoutes.ingestion.permission)
  );
  const { ingestionScheduleIsOpen, setIngestionScheduleIsOpen } = useIngestionSchedule();
  const {
    ingestionSchedule,
    isLoading,
    hasError,

    isUpdating,
    hasErrorUpdating,
    isDeleting,
    hasErrorDeleting,

    isSuccessfulDeleting,
    isSuccessfulUpdating,

    updateSchedule,
    deleteSchedule,
    fetchSchedule,
  } = useGlobalIngestionSchedule();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    // reset form with updated ingestionSchedule
    reset({ ingestionSchedule });
  }, [ingestionSchedule, reset]);

  const onCancel = () => {
    setIngestionScheduleIsOpen(false);
  };

  const onSubmit = (data: IngestionScheduleFormData) => {
    if (data.ingestionSchedule[0].override) {
      // if ingestion checkmark is checked
      updateSchedule(data, {
        onSuccess: () => {
          onCancel();
        },
      });
    } else {
      deleteSchedule(data.ingestionSchedule[0].id, {
        onSuccess: () => {
          onCancel();
        },
      });
    }
  };
  const infoBannerContent: { variant: TInfoBannerVariant | undefined; label: string } =
    useMemo(() => {
      switch (true) {
        case isUpdating || isDeleting:
          return { variant: 'loading', label: 'Saving ingestion schedule...' };
        case hasErrorUpdating || hasErrorDeleting:
          return {
            variant: 'error',
            label: 'The ingestion schedule was not saved.  Please try again.',
          };
        case isSuccessfulUpdating || isSuccessfulDeleting:
          return {
            variant: 'success',
            label: `The ingestion schedule was ${isSuccessfulUpdating ? 'updated' : 'deleted'}.`,
          };
        default:
          return { variant: undefined, label: '' };
      }
    }, [
      isUpdating,
      isDeleting,
      hasErrorUpdating,
      hasErrorDeleting,
      isSuccessfulUpdating,
      isSuccessfulDeleting,
    ]);

  if (!ingestionScheduleIsOpen) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="tw:py-4" data-testid="ingestion-schedule-loading">
        <LoadingIndicator isLoading heightClass="global-ingestion-schedule-form" />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:shrink-0">
        <InfoBanner
          className="tw:top-0 tw:left-0 tw:w-full"
          label={'Ingestion failed to load'}
          testId="ingestion-schedule-info-banner"
          variant={'error'}
        />
        <EmptyListSection showRetry retryFn={fetchSchedule} />
      </div>
    );
  }

  if (!canEdit) {
    return <IngestionScheduleSummary ingestionSchedule={ingestionSchedule?.[0]} />;
  }

  return (
    <Animated
      animationIn="fadeIn"
      animationOut="fadeOut"
      animationInDuration={500}
      animationOutDuration={500}
      onClick={() => {}}
      innerRef={React.createRef()}
      className="tw:shrink-0"
    >
      <form onSubmit={handleSubmit(onSubmit as any)} className="tw:shrink-0 relative">
        <FormDivider title="Ingestion" />
        {infoBannerContent.variant && (
          <InfoBanner
            label={infoBannerContent?.label}
            testId="ingestion-schedule-info-banner"
            variant={infoBannerContent?.variant}
          />
        )}
        <IngestionOverrideForm
          errors={errors}
          register={register}
          setValue={setValue}
          control={control}
          watch={watch}
          ingestionSchedule={ingestionSchedule}
          isStandAlone
          optionalHeader
        />
        <ActionButtonsContainer className="justify-end" isLegacy={false}>
          <CancelButton data-testid="warning-modal-cancel-button" onClick={onCancel} />
          <SubmitButton data-testid="update-sched-btn" />
        </ActionButtonsContainer>
      </form>
    </Animated>
  );
}
